<template>
    <div class="pathogenesis-analysis">
        <div v-if="hasStackedData || hasOverviewData" class="chart-row">
            <div v-if="hasStackedData" class="chart-card w-1-2">
                <p class="chart-card-tit">{{ stackedTitle }}</p>
                <disease-chart :option="stackedOption" height="360px" />
            </div>
            <div v-if="hasOverviewData" class="chart-card w-1-2">
                <p class="chart-card-tit">{{ overviewTitle }}</p>
                <disease-chart :option="overviewOption" height="360px" />
            </div>
        </div>
        <div v-if="detailCharts.length" class="detail-grid">
            <div
                v-for="item in detailCharts"
                :key="item.field"
                class="chart-card"
            >
                <p class="chart-card-tit">{{ item.title }}</p>
                <disease-chart :option="item.option" height="300px" />
            </div>
        </div>
    </div>
</template>

<script>
import DiseaseChart from './disease_chart.vue';
import { PATHOGENESIS_TYPES } from '../doctorData';
import { buildStackedTooltip } from './buildStackedTooltip';

const STACK_COLORS = ['#448EF6', '#1EB273', '#F39C12', '#8E44AD', '#E91E63', '#00BCD4', '#F44336'];
const DETAIL_COLORS = ['#db2777', '#7c3aed', '#ea580c', '#165DFF'];

function buildStackedOption(stacked) {
    const xAxis = stacked.xAxis || [];
    const seriesList = stacked.series || [];
    const sumList = stacked.sumList || [];
    // 默认展示全部分类，滑块可自由缩放，不限制可见分类数量。
    const zoomWindow = {
        xAxisIndex: 0,
        startValue: 0,
        endValue: Math.max(0, xAxis.length - 1),
        maxValueSpan: 100, 
    };
    const series = seriesList.map((item, index) => ({
        name: item.name,
        type: 'bar',
        stack: 'total',
        // 柱宽随类目槽位收缩；仅限制最大宽度，避免缩小后相邻柱相互覆盖。
        barMaxWidth: 28,
        barCategoryGap: '30%',
        emphasis: { focus: 'series' },
        itemStyle: {
            color: STACK_COLORS[index % STACK_COLORS.length],
        },
        data: item.data || [],
    }));
    if (sumList.length) {
        series.push({
            name: '合计',
            type: 'bar',
            stack: 'total',
            barMaxWidth: 28,
            data: xAxis.map(() => 0),
            itemStyle: { color: 'transparent' },
            emphasis: { disabled: true },
            tooltip: { show: false },
            label: {
                show: true,
                position: 'top',
                color: '#5A6779',
                fontSize: 12,
                formatter(params) {
                    return sumList[params.dataIndex] || '';
                },
            },
        });
    }
    return {
        tooltip: buildStackedTooltip(),
        legend: {
            type: 'scroll',
            left: 16,
            right: 16,
            bottom: 0,
            itemWidth: 14,
            itemHeight: 10,
            tooltip: { show: true },
            data: seriesList.map((item) => item.name),
            textStyle: { color: '#5A6779', fontSize: 12, width: 120, overflow: 'truncate' },
        },
        grid: {
            left: 16,
            right: 16,
            top: 28,
            bottom: 90,
            containLabel: true,
        },
        dataZoom: [
            { ...zoomWindow, type: 'inside' },
            { ...zoomWindow, type: 'slider', bottom: 34, height: 18 },
        ],
        xAxis: {
            type: 'category',
            data: xAxis,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#E8EEF8' } },
            axisLabel: {
                color: '#333',
                fontSize: 12,
                // 显示每个分类的标签，分类超过 8 个时倾斜 30 度。
                interval: 0,
                hideOverlap: false,
                width: 72,
                overflow: 'truncate',
                rotate: xAxis.length > 8 ? 30 : 0,
                margin: 12,
            },
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#E8EEF8' } },
            axisLabel: { color: '#5A6779' },
        },
        series,
    };
}

function buildColumnOption(list, color) {
    const data = list || [];
    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        grid: {
            left: 16,
            right: 16,
            top: 24,
            bottom: 8,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: data.map((item) => item.name),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#E8EEF8' } },
            axisLabel: {
                color: '#333',
                fontSize: 12,
                interval: 0,
                rotate: 20,
            },
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#E8EEF8' } },
            axisLabel: { color: '#5A6779' },
        },
        series: [
            {
                type: 'bar',
                data: data.map((item) => item.value),
                barWidth: 22,
                itemStyle: {
                    color,
                    borderRadius: [6, 6, 0, 0],
                },
                label: {
                    show: true,
                    position: 'top',
                    color: '#5A6779',
                    fontSize: 11,
                },
            },
        ],
    };
}

function buildBarOption(list, color) {
    const data = [...(list || [])].reverse();
    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        grid: {
            left: 16,
            right: 40,
            top: 8,
            bottom: 8,
            containLabel: true,
        },
        xAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#E8EEF8' } },
            axisLabel: {
                color: '#5A6779',
                hideOverlap: true,
                margin: 10,
            },
        },
        yAxis: {
            type: 'category',
            data: data.map((item) => item.name),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#E8EEF8' } },
            axisLabel: {
                color: '#333',
                fontSize: 12,
                width: 120,
                overflow: 'truncate',
                margin: 10,
            },
        },
        series: [
            {
                type: 'bar',
                data: data.map((item) => item.value),
                barWidth: 14,
                barCategoryGap: '36%',
                itemStyle: {
                    color,
                    borderRadius: [0, 6, 6, 0],
                },
                label: {
                    show: true,
                    position: 'right',
                    color: '#5A6779',
                    fontSize: 11,
                    distance: 6,
                },
            },
        ],
    };
}

export default {
    name: 'PathogenesisAnalysis',
    components: {
        DiseaseChart,
    },
    props: {
        chartData: { type: Object, default: () => ({}) },
    },
    computed: {
        stackedData() {
            // 堆叠图配置位于 pathogenesisStackedBar.data。
            return (this.chartData.pathogenesisStackedBar && this.chartData.pathogenesisStackedBar.data) || {};
        },
        hasStackedData() {
            return (this.stackedData.xAxis || []).length > 0 &&
                (this.stackedData.series || []).some((item) => (item.data || []).length > 0);
        },
        hasOverviewData() {
            return (this.chartData.tcmEtiologyPathogenesisTop10 || []).length > 0;
        },
        stackedTitle() {
            const title = this.stackedData.title || '病因病机柱状堆积图';
            const total = this.stackedData.sumNumber;
            return total ? `${title}（合计 ${total}）` : title;
        },
        overviewTitle() {
            return '中医病因病机 Top10';
        },
        stackedOption() {
            return buildStackedOption(this.stackedData);
        },
        overviewOption() {
            return buildColumnOption(this.chartData.tcmEtiologyPathogenesisTop10 || [], '#165DFF');
        },
        detailCharts() {
            return PATHOGENESIS_TYPES.slice(1).map((item, index) => {
                const list = this.chartData[item.field] || [];
                return {
                    ...item,
                    title: item.title + ' Top10',
                    hasData: list.length > 0,
                    option: buildBarOption(list, DETAIL_COLORS[index % DETAIL_COLORS.length]),
                };
            }).filter((item) => item.hasData);
        },
    },
};
</script>

<style lang="less" scoped>
.pathogenesis-analysis {
    .chart-row {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .detail-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
    }

    .chart-card {
        min-width: 0;
        background: #fff;
        border: 1px solid #E8EEF8;
        border-radius: 10px;
        padding: 12px 14px 16px;
        box-sizing: border-box;

        &.w-1-2 {
            flex: 1 1 0;
        }

    }

    .chart-card-tit {
        margin: 0 0 8px;
        font-size: 14px;
        font-weight: bold;
        color: #0C55AA;
        line-height: 20px;
    }
}
</style>
